<template>
  <table class="w-100 table info-table table-sm totals-table">
    <tr v-if="!hideHammer">
      <td class="border-top-0"><b>{{ $t('Hammer') }}</b></td>
      <td class="border-top-0">{{ totalHammerPrice | asCurrency(currency) }}</td>
    </tr>
    <tr v-if="!hidePremium">
      <td><b>{{ $t('Premium') }}</b></td>
      <td>{{ totalInclusivePremium | asCurrency(currency) }}</td>
    </tr>
    <tr v-if="!hidePremiumVat">
      <td><b>{{ dynamicTexts.inputVat }}</b></td>
      <td>{{ totalInputVat | asCurrency(currency) }}</td>
    </tr>
    <tr v-if="!hideImportTax && !isAvalaraEnabled">
      <td><b>{{ $t('Import Tax') }}</b></td>
      <td>{{ totalImportTax | asCurrency(currency) }}</td>
    </tr>
    <template v-if="model.enable_shipping && !hideShipping">
      <tr>
        <td><b>{{ $t('Shipping') }}</b></td>
        <td>{{ shippingAmount | asCurrency(currency) }}</td>
      </tr>
      <tr v-if="!hideShippingVat">
        <td><b>{{ dynamicTexts.shippingVat }}</b></td>
        <td>{{ shippingVat | asCurrency(currency) }}</td>
      </tr>
    </template>
    <tr>
      <td><b>{{ getLabel('miscItem') }}</b></td>
      <td>{{ totalMiscCharge | asCurrency(currency) }}</td>
    </tr>
    <tr v-if="!hideMiscChargeVat">
      <td><b>{{ getLabel('miscItemVat') }}</b></td>
      <td>{{ totalMiscVat | asCurrency(currency) }}</td>
    </tr>
    <template v-for="(charge,index) in buyerExtraCharges" v-if="!hideExtraCharges">
      <tr :key="`total_buyer_extra_charge${index}`">
        <td><b>{{ charge ? charge.name : '' }}</b></td>
        <td v-if="charge">{{ charge.totalAmount | asCurrency(currency) }}</td>
        <td v-else></td>
      </tr>
      <tr v-if="charge" :key="`total_buyer_extra_charge_vat${index}`">
        <td><b>{{ charge ? `${dynamicTexts.vatOn} ${charge.name}` : '' }}</b></td>
        <td v-if="charge">{{ charge.totalVat | asCurrency(currency) }}</td>
        <td v-else></td>
      </tr>
    </template>
    <tr class="total-amount">
      <td><b>{{ $t('Total') }}</b></td>
      <td>{{ totalAmount | asCurrency(currency) }}</td>
    </tr>
  </table>
</template>

<script>
  import BaseModel from "@/core/components/input-widget/BaseModel";
  import _ from 'lodash'
  import {createNamespacedHelpers} from "vuex";

  const {mapState: globalMapState, mapGetters: globalMapGetters} = createNamespacedHelpers('global');
  export default {
    name: "InvoiceTotalsTable",
    props: {
      model: BaseModel,
      miscellaneousItems: Array,
      financialItems: Array,
      currency: {
        type: String,
        default: null
      },
      hidePremium: {type: Boolean, default: false},
      hideHammer: {type: Boolean, default: false},
      hidePremiumVat: {type: Boolean, default: false},
      hideShipping: {type: Boolean, default: false},
      hideShippingVat: {type: Boolean, default: false},
      hideMiscChargeVat: {type: Boolean, default: false},
      skipShippingVatFromTotal: {type: Boolean, default: false},
      skipMiscChargeVatFromTotal: {type: Boolean, default: false},
      hideImportTax: {type: Boolean, default: false},
      hideExtraCharges: {type: Boolean, default: false},
      customLabels: {
        type: Object,
        required: false,
        default: () => {
          return {}
        }
      },
    },
    computed: {
      ...globalMapGetters(['isAvalaraEnabled']),
      ...globalMapState({
        dynamicTexts: state => state.dynamicTexts,
      }),
      totalHammerPrice() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.buyer_financial_price || 0), 0);
      },
      totalMiscCharge() {
        return this.miscellaneousItems.reduce((a, b) => a + parseFloat(b.amount || 0), 0);
      },
      totalMiscVat() {
        return this.miscellaneousItems.reduce((a, b) => a + parseFloat(b.vat || 0), 0);
      },
      totalInclusivePremium() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.premium || 0), 0);
      },
      totalInputVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.input_vat || 0), 0);
      },
      totalImportTax() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.import_tax || 0), 0);
      },
      shippingAmount() {
        return parseFloat(this.model.shipping_amount || 0);
      },
      shippingVat() {
        return parseFloat(this.model.shipping_vat || 0);
      },
      buyerExtraCharges() {
        let charges = this.financialItems.map(fi => {
          let currentCharges = [];
          if (fi.unsaved_buyer_financial_item_charges) {
            currentCharges = fi.unsaved_buyer_financial_item_charges
          } else if (fi.buyer_financial_item_charges) {
            currentCharges = fi.buyer_financial_item_charges
          }

          let deletedFinancialItemChargeUuids = [];
          if (fi.buyer_financial_item_charges_to_delete) {
            deletedFinancialItemChargeUuids = fi.buyer_financial_item_charges_to_delete.map(fic => fic.uuid);
          }
          currentCharges = currentCharges.filter(fic => !deletedFinancialItemChargeUuids.includes(fic.uuid));

          return currentCharges;
        });

        let unitedChargesArr = [];
        charges.map(c => c.map(i => {
          unitedChargesArr.push(i)
        }));

        let extraCharges = [];
        let groupedChargesByUuid = _.groupBy(unitedChargesArr, 'charge_uuid');
        Object.keys(groupedChargesByUuid).map(key => {
          extraCharges.push({
            name: groupedChargesByUuid[key][0].charge.name,
            totalAmount: groupedChargesByUuid[key].reduce((a, b) => a + parseFloat(b.amount || 0), 0),
            totalVat: groupedChargesByUuid[key].reduce((a, b) => a + parseFloat(b.vat || 0), 0)
          })
        });
        return extraCharges
      },
      totalBuyerExtraCharges() {
        return this.buyerExtraCharges.reduce((a, b) => a + parseFloat(b.totalAmount || 0) + parseFloat(b.totalVat || 0), 0)
      },
      totalDuties() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.duties || 0), 0);
      },
      totalAmount() {
        let amount = this.totalHammerPrice + this.totalInputVat + this.totalImportTax + this.totalInclusivePremium +
          this.totalMiscCharge + this.shippingAmount + this.totalBuyerExtraCharges + this.totalDuties;

        if (!this.skipShippingVatFromTotal) {
          amount = amount + this.shippingVat
        }

        if (!this.skipMiscChargeVatFromTotal) {
          amount = amount + this.totalMiscVat
        }

        return amount;
      },
    },
    methods: {
      getLabel(key) {
        let obj = {
          miscItem: this.$i18n.t('Misc. Charges'),
          miscItemVat: this.dynamicTexts.vatOnMiscCharges
        }
        return this.customLabels[key] ? this.customLabels[key] : obj[key]
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .totals-table {
    tr {
      td {
        text-align: right;
      }

      td:nth-child(2) {
        width: 30%;
      }
    }

    .total-amount {
      td {
        border-top: 1px solid $primary;
      }
    }
  }
</style>
